import { useContext, useEffect, useMemo, useState } from "react";
import InvitedFriend from "./InvitedFriends/InvitedFriend";
import Offers from "../Offers";
import ReceivedVipTableOffers from "./ReceivedVipTableOffers";
import { defaultImgs } from "../../utils/defaultImages";
import {
    BackendGroupInvitationStatus,
    GroupInvitationStatus,
} from "../../api/enums/GroupInvitationStatus";
import { HostChatMessage, Invitation, PromotersOffer, appFriends } from "../../types";
import { DateFormat, formatDate } from "../../utils/datePareser";
import { getOrderedInvitations } from "../../utils/utils";
import { localStorageKeys } from "../../utils/localStorageKeys";
import AuthenticationResponse from "../../api/responses/AuthenticationResponse";
import styles from "./myPartyDetail.module.scss";
import { iconsImgs } from "../../utils/icons";
import { useNavigate } from "react-router-dom";
import { routes } from "../../routes/routes";
import GroupInvitationClient from "../../api/responses/GroupInvitationStatusResponse";
import AuthClient from "../../api/AuthClient";
import DeleteModal from "./DeleteModal";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import AddFriendsOptionsModal from "../AddFriends/Modals/AddFriendsOptionsModal";
import BookingType from "../../api/enums/BookingType";
import Header from "../Header";
import GetFriendsInAppApi from "../../api/GetFriendsInAppApi";
import { parseAppFriendsResponse } from "../../api/parseResponse/parseAppFriendsResponse";
import getPartyClubDisplayInfo from "../../utils/getPartyClubDisplayInfo";
import { getClubPhoto } from "../../utils/getMedia";
import { PartiesContext } from "../../GlobalContext/partiesContext";
import GroupStatus from "../../api/enums/GroupStatus";
import { Spinner } from "react-bootstrap";
import GroupsClient from "../../api/GroupsClient";
import JoinVipUpdateApi from "../../api/JoinVipUpdateApi";
import { updateJoinVipVisibilityAndRefresh } from "./joinVipVisibilityActions";
import { syncMyPartiesAndSelectedPartyFromGroupId } from "../../utils/fetchAndSetMyPartiesInfo";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../query/queryKeys";
import { useChatMessages } from "../../hooks/useServerDataQueries";

type RequestState = "idle" | "loading" | "success" | "error";

const MyPartyDetail = () => {
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const handleClose = () => setShowModal(false);

    const [showFriendsModal, setFriendsShowModal] = useState(false);
    const handleShowModal = () => setFriendsShowModal(true);
    const handleCloseModal = () => setFriendsShowModal(false);

    const [chatMessages, setChatMessages] = useState<HostChatMessage[]>();
    const [appFriends, setAppFriends] = useState<appFriends>();
    const [selectedInvitationStatus, setSelectedInvationStatus] =
        useState<string>("");
    const [joinVipUpdateRequestState, setJoinVipUpdateRequestState] =
        useState<RequestState>("idle");
    const [showGroupToOthers, setShowGroupToOthers] = useState(false);

    const authClient = new AuthClient();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {
        selectedParty: selectedPartyInfo,
        setSelectedPartyContextData,
        setMyPartiesInfoContextData,
    } = useContext(PartiesContext);

    useEffect(() => {
        setShowGroupToOthers(Boolean(selectedPartyInfo?.party.isPublicTableBooking));
    }, [selectedPartyInfo?.party.isPublicTableBooking]);

    useEffect(() => {
        // This effect runs after selectedPartyInfo is available
        if (selectedPartyInfo?.promotersOffers.length) {
            selectedPartyInfo.promotersOffers.forEach((promoterOffer) => {
                promoterOffer.offers.forEach((offer) => {
                    offer.partyDate = selectedPartyInfo.party.partyDate;
                });
            });
        }

        const getFriendsInAppApi = new GetFriendsInAppApi(authClient);

        getFriendsInAppApi.getFriendsInApp().then((response) => {
            const appFriends = parseAppFriendsResponse(response?.data);
            setAppFriends(appFriends);
        });
    }, [selectedPartyInfo]);

    const storedLoginResponse =
        sessionStorage.getItem(localStorageKeys.loginResponse) ??
        localStorage.getItem(localStorageKeys.loginResponse) ??
        '{}';

    const userInfo = JSON.parse(storedLoginResponse) as AuthenticationResponse;

    const partyDate = useMemo(() => {
        if (!selectedPartyInfo) return "";
        const date = new Date(selectedPartyInfo?.party?.partyDate);
        return formatDate(date, false, DateFormat.Short);
    }, [selectedPartyInfo?.party?.partyDate]);

    const clubDisplayInfo = useMemo(() => {
        if (!selectedPartyInfo) {
            return {
                clubName: "?",
                imageSrc: defaultImgs.ClubImg,
            };
        }

        return getPartyClubDisplayInfo(selectedPartyInfo);
    }, [selectedPartyInfo]);

    const requestedClubs = useMemo(() => {
        if (!selectedPartyInfo) {
            return []
        }
        return selectedPartyInfo.requestedListings.map(listing => {
            return {
                id: listing.id,
                clubName: listing.title ?? listing.clubName ?? "?",
                imageSrc: listing.picture ?? getClubPhoto({ clubName: listing.clubName, cityName: selectedPartyInfo.party.city }),
            };
        });

    }, [selectedPartyInfo]);

    const handleClubImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const image = event.currentTarget;
        if (!image.dataset.fallback) {
            image.dataset.fallback = "1";
            image.src = defaultImgs.ClubImg;
        }
    };

    const orderedInvitations = useMemo(() => {
        if (!selectedPartyInfo) return [];
        const invitations = getOrderedInvitations(userInfo, selectedPartyInfo.invitations);
        const organiser = invitations.find((invitation) => invitation.isGroupAdmin);

        if (!organiser) {
            return invitations;
        }

        return [
            organiser,
            ...invitations.filter((invitation) => invitation.id !== organiser.id),
        ];
    }, [selectedPartyInfo?.invitations, userInfo]);

    const inviter = useMemo(() => {
        return orderedInvitations.find((invitation) => invitation.isGroupAdmin);
    }, [orderedInvitations]);

    const currentUserInvitation = useMemo(() => {
        if (!selectedPartyInfo || !userInfo)
            return undefined;

        return selectedPartyInfo.invitations.find(
            (invitation) =>
                invitation.firstName === userInfo.firstName &&
                invitation.lastName === userInfo.lastName
        );
    }, [selectedPartyInfo, userInfo]);

    const isCurrentUserGroupAdmin = currentUserInvitation?.isGroupAdmin ?? false;

    useEffect(() => {
        setSelectedInvationStatus(currentUserInvitation?.status ?? "");
    }, [currentUserInvitation?.status]);

    const selectedFriendIds = useMemo(
        () =>
            selectedPartyInfo?.invitations
                .filter((invitation) => !invitation.isGroupAdmin)
                .map((invitation) => invitation.id) ?? [],
        [selectedPartyInfo?.invitations]
    );

    const selectedPromoterOffer = useMemo(() => {
        if (!selectedPartyInfo) return undefined;
        return selectedPartyInfo.promotersOffers.find((promoterOffer) =>
            promoterOffer.offers.some((offer) => offer.isAccepted === true)
        );
    }, [selectedPartyInfo?.promotersOffers]);

    const acceptedOffer = useMemo(() => {
        if (!selectedPromoterOffer) return undefined;
        return selectedPromoterOffer?.offers.find(
            (offer) => offer.isAccepted === true
        );
    }, [selectedPromoterOffer?.offers]);
    const { data: queriedChatMessages } = useChatMessages(acceptedOffer?.id);

    useEffect(() => {
        if (queriedChatMessages) {
            setChatMessages(queriedChatMessages);
        }
    }, [queriedChatMessages]);

    const anyAcceptedOffer = useMemo(() => {
        if (!selectedPartyInfo) return false;
        return selectedPartyInfo.promotersOffers.some(x => x.offers.some((offer) => offer.isAccepted === true));
    }, [selectedPartyInfo?.promotersOffers]);

    const nonVipPromotersOffers = useMemo(() => {
        if (!selectedPartyInfo) return [];
        return selectedPartyInfo.promotersOffers
            .map((entry) => ({
                ...entry,
                offers: entry.offers.filter((offer) => offer.offerType !== "JoinVIPTable"),
            }))
            .filter((entry) => entry.offers.length > 0);
    }, [selectedPartyInfo?.promotersOffers]);

    if (!selectedPartyInfo) {
        return <div>Party not found</div>;
    }

    const partyId = selectedPartyInfo.party.id;
    const groupId = partyId;
    const invitationProfileId = currentUserInvitation?.id;
    const groupInvitationClient = new GroupInvitationClient(authClient);
    const groupsClient = new GroupsClient(authClient);

    const getInvitationButtonClass = (status: GroupInvitationStatus) =>
        `btn btn-sm ${selectedInvitationStatus === status ? "btn-outline-light" : "btn-outline-secondary"} flex-fill`;

    const handleInvitationStatusChange = async (
        status: GroupInvitationStatus
    ) => {
        const previousStatus = currentUserInvitation?.status ?? "";
        setSelectedInvationStatus(status);
        if (status === GroupInvitationStatus.Declined && isCurrentUserGroupAdmin) {
            setModalMessage('Would you like to delete this group?');
            setShowModal(true);
        }
        let backendStatus;
        switch (status) {
            case GroupInvitationStatus.Declined:
                backendStatus = BackendGroupInvitationStatus.Declined;
                break;
            case GroupInvitationStatus.Maybe:
                backendStatus = BackendGroupInvitationStatus.Maybe;
                break;
            case GroupInvitationStatus.Accepted:
                backendStatus = BackendGroupInvitationStatus.Accepted;
                break;
            default:
                console.error("Unexpected status:", status);
                return;
        }

        if (!invitationProfileId) {
            console.error("Invitation profile not found for current user");
            return;
        }

        try {
            await groupInvitationClient.updateInvitationStatus(
                groupId,
                invitationProfileId,
                backendStatus
            );
            await syncMyPartiesAndSelectedPartyFromGroupId(
                authClient,
                groupId,
                setSelectedPartyContextData,
                setMyPartiesInfoContextData
            );
            void queryClient.invalidateQueries({ queryKey: queryKeys.myParties() });
            void queryClient.invalidateQueries({ queryKey: queryKeys.myParty(groupId) });
        } catch (error) {
            console.error("Error updating invitation status", error);
            setSelectedInvationStatus(previousStatus);
        }
    };

    const goToOfferDetailsPage = (
        promoterOffer: PromotersOffer | undefined,
        cityName: string
    ) => {
        if (promoterOffer) {
            const offer = promoterOffer.offers.find(
                (offer) => offer.isAccepted === true
            );

            if (cityName && partyId && offer?.isAccepted) {
                navigate(routes.OfferDetails(partyId, offer.id), {
                    state: {
                        promoter: promoterOffer.promoter,
                        city: cityName,
                        offer: offer,
                    },
                });
            }
        }
    };

    const handleDelete = () => {
        if (!isCurrentUserGroupAdmin) {
            return;
        }

        setModalMessage('Are you sure you want to permanently remove this group?');
        setShowModal(true);
    };

    const handleShowGroupToOthersChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!isCurrentUserGroupAdmin) {
            return;
        }

        if (joinVipUpdateRequestState === "loading") {
            return;
        }

        const nextChecked = event.target.checked;
        setShowGroupToOthers(nextChecked);
        setJoinVipUpdateRequestState("loading");

        const joinVipUpdateApi = new JoinVipUpdateApi(authClient);

        try {
            const refreshedParty = await updateJoinVipVisibilityAndRefresh({
                groupId,
                updateJoinVipVisibility: (currentGroupId) => joinVipUpdateApi.updateJoinVip(currentGroupId),
                getParsedMyPartyById: (currentGroupId) => groupsClient.getParsedMyPartyById(currentGroupId),
                setSelectedPartyContextData,
                setMyPartiesInfoContextData,
            });

            setShowGroupToOthers(Boolean(refreshedParty.party.isPublicTableBooking));
            queryClient.setQueryData(queryKeys.myParty(groupId), refreshedParty);
            void queryClient.invalidateQueries({ queryKey: queryKeys.myParties() });
            setJoinVipUpdateRequestState("success");
        } catch (error) {
            console.error("Error updating join vip visibility", error);
            setShowGroupToOthers(Boolean(selectedPartyInfo.party.isPublicTableBooking));
            setJoinVipUpdateRequestState("error");
        } finally {
            setTimeout(() => {
                setJoinVipUpdateRequestState("idle");
            }, 800);
        }
    };

    const openChats = () => {
        if (!isCurrentUserGroupAdmin) {
            return;
        }

        const userInfo: AuthenticationResponse = JSON.parse(
            (
                sessionStorage.getItem(localStorageKeys.loginResponse) ??
                localStorage.getItem(localStorageKeys.loginResponse) ??
                "{}"
            )
        );
        const myInfo = selectedPartyInfo.invitations.find(
            (el: Invitation) =>
                el.firstName === userInfo.firstName &&
                el.lastName === userInfo.lastName
        );

        navigate(routes.ChatPages, {
            state: {
                messages: chatMessages, 
                offer: acceptedOffer, 
                groupAdminName: `${inviter?.firstName ?? ""} ${inviter?.lastName ?? ""}`.trim(),
                groupAdminId: inviter?.id,
                promoterName: selectedPromoterOffer?.promoter.FirstName,
                promoterId: selectedPromoterOffer?.promoter.id,
                currentUserId: myInfo?.id, 
                bookingType: selectedPartyInfo.party.bookingType
            }
        });
    }

    return (
        <>
            <AddFriendsOptionsModal showModal={showFriendsModal} handleCloseModal={handleCloseModal} showWarningModal={false} groupId={groupId} appFriends={appFriends} selectedFriendIds={selectedFriendIds} />

            <Header />
            <div className="centerGroup">
                <div className="desktopUI">
                    <div className="row userProfile_Header">
                        <Breadcrumbs to={routes.Root} />
                        <div className="d-flex justify-content-end space col-6">
                            {isCurrentUserGroupAdmin && (
                                <button className="btn btn-sm btn-outline-danger" onClick={handleDelete} >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.myPartyDetailPage}>
                <div className={styles.myPartyContainer}>
                    <div className={styles.date}>{partyDate}</div>
                    {(BookingType[selectedPartyInfo.party.bookingType]).toString() === BookingType.GuestList.toString()
                        && (
                            <>
                                <div className="text-white mt-2">
                                    Are You Going?
                                    <div className="d-flex justify-content-between align-items-center mt-3 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleInvitationStatusChange(GroupInvitationStatus.Declined)}
                                            className={getInvitationButtonClass(GroupInvitationStatus.Declined)}
                                        >
                                            Not Going
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleInvitationStatusChange(GroupInvitationStatus.Maybe)}
                                            className={getInvitationButtonClass(GroupInvitationStatus.Maybe)}
                                        >
                                            Maybe
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleInvitationStatusChange(GroupInvitationStatus.Accepted)}
                                            className={getInvitationButtonClass(GroupInvitationStatus.Accepted)}
                                        >
                                            Going
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                    <div className={styles.clubImageContainer}>
                        <img
                            className={styles.clubImage}
                            src={clubDisplayInfo.imageSrc}
                            alt={`clubImg`}
                            onError={handleClubImageError}
                        />

                        <span className={styles.clubName}>
                            {clubDisplayInfo.clubName}
                        </span>
                    </div>

                    <div className={styles.setTitleCityBox}>
                        <span className={styles.city}>{selectedPartyInfo.party.name}</span>
                        <span className={styles.city}>{selectedPartyInfo.party.city}</span>
                    </div>
                    {isCurrentUserGroupAdmin && (
                        <div className={styles.showToOtherGroupsContainer + ' form-check align-items-left col d-flex mb-3'}>
                            <input
                                className="form-check-input checkC shadow-none"
                                type="checkbox"
                                checked={showGroupToOthers}
                                onChange={(event) => { void handleShowGroupToOthersChange(event); }}
                                disabled={joinVipUpdateRequestState === "loading"}
                            />
                            <div className='align-content-center col d-flex'>
                                <label className={`yellowTxt col text-left ${styles.showToOtherGroupsLabel}`}>
                                    &nbsp;Allow guest-list groups to discover your table
                                    {joinVipUpdateRequestState === "loading" ? (
                                        <span className={styles.showToOtherGroupsLoading}>
                                            <Spinner animation="border" variant="warning" size="sm" />
                                            Updating...
                                        </span>
                                    ) : null}
                                </label>
                            </div>
                        </div>
                    )}
                    <div className={styles.buttonsContainer}>
                        <div className={styles.firstSection}>
                            {selectedPartyInfo.promotersOffers.some((promoter) =>
                                promoter.offers.some((offer) => offer.isAccepted === true)
                            ) && (
                                    <>
                                        {isCurrentUserGroupAdmin && <button className={styles.buttonWithIcon} onClick={openChats}>
                                            <div>
                                                <span className={styles.buttonText}>
                                                    Chat with host
                                                    <img
                                                        src={iconsImgs.ChatButton}
                                                        alt="Chat Icon"
                                                        className={styles.chatIcon}
                                                    />
                                                </span>

                                                <span className={styles.alertText}>
                                                    YOU NEED TO CHAT TO HOST TO ARRANGE THE ENTRY
                                                </span>
                                            </div>
                                        </button>}

                                        <button
                                            className={styles.buttonWithIcon}
                                            onClick={() => {
                                                goToOfferDetailsPage(
                                                    selectedPromoterOffer,
                                                    selectedPartyInfo.party.city
                                                );
                                            }}
                                        >
                                            View Accepted Offer
                                        </button>

                                        <div className={styles.warningContainer}>
                                            <p className={styles.warningText}>Remember to bring ID</p>
                                            <p className={styles.warningText}>Dress to impress</p>
                                            <p className={styles.warningText}>
                                                Tag photo with @ClubbableApp
                                            </p>
                                            <span className={styles.featureTxt}>(and we may feature you on our feed)</span>
                                        </div>
                                    </>
                                )}

                            {
                                (BookingType[selectedPartyInfo.party.bookingType]).toString() === BookingType.TableBooking.toString()
                                    && (selectedPartyInfo.party.status === GroupStatus.Accepted || selectedPartyInfo.party.status === GroupStatus.Looking) ?
                                    <div className={styles.divcontainer}>
                                        <p className={styles.warningPara}>
                                            Your table is not booked until your host confirms in chat that
                                            it is. The club will not have your reservation before confirmed
                                            in chat. You need to allow some time for host to get back to you
                                            before you arrive at the club.
                                        </p>
                                    </div> : ""
                            }

                            {
                                (BookingType[selectedPartyInfo.party.bookingType]).toString() === BookingType.GuestList.toString() ?
                                    <div className={styles.divcontainer}>
                                        <p className={styles.warningPara}>
                                            You are not on the list until you have accepted an offer and promoter
                                            said you are on the list. Groups where all guests have the app and
                                            several photos get more offers.
                                        </p>
                                    </div> : ""
                            }
                        </div>
                    </div>

                    <div className={styles.offerSection}>
                        <ReceivedVipTableOffers
                            partyId={partyId}
                            cityName={selectedPartyInfo.party.city}
                            promotersOffers={selectedPartyInfo.promotersOffers}
                        />
                        {!anyAcceptedOffer &&
                            <Offers
                                cityName={selectedPartyInfo.party.city}
                                allOffers={nonVipPromotersOffers}
                            />
                        }
                    </div>

                    <div className={styles.buttonsContainer}>
                        <div className={styles.line} />
                        <p className={styles.alterMessage}>
                            Make sure all guests have a photo
                        </p>
                        {isCurrentUserGroupAdmin && <div className={styles.secondSectionAccepted}>
                            <button onClick={handleShowModal}>+Add Friends</button>
                            {selectedPartyInfo.party.status === GroupStatus.Looking
                                && <button onClick={() => navigate(routes.RequestClub(partyId))}>Clubs Requested</button>
                            }
                        </div>}
                    </div>

                    <h3 className={styles.invitedFriendsTitle}>INVITED FRIENDS</h3>
                    {orderedInvitations.map((el) => (
                        <div key={el.id} className={styles.invitedFriendsContainer}>
                            <InvitedFriend
                                invitedFriend={el}
                                inviter={inviter}
                                isFirst={el.isGroupAdmin}
                                groupId={partyId}
                            />
                        </div>
                    ))}
                    {requestedClubs.length > 1 && <>
                        <h3 className={styles.invitedFriendsTitle}>CLUBS REQUESTED</h3>
                        <div className="width-100 d-flex flex-column gap-3 text-left">
                            {
                                requestedClubs.map((club) => (<div key={club.id} className="m-0 align-items-center col d-flex">
                                    <img
                                        src={club.imageSrc}
                                        alt="clubImg"
                                        className="clubImg"
                                        onError={handleClubImageError}
                                    />
                                    <div className="align-content-center col d-flex">
                                        <label className="yellowTxt col">
                                            {club.clubName}
                                        </label>
                                    </div>
                                </div>))
                            }

                        </div>
                    </>}
                </div>
            </div>

            <DeleteModal
                show={showModal}
                message={modalMessage}
                onHide={handleClose}
                offerId={acceptedOffer?.id}
                groupId={partyId}
                anyAcceptedOffer={anyAcceptedOffer}
            />
        </>
    );
};

export default MyPartyDetail;
